<template>
    <div>
        <FormHeading>
            What is the property value?
            <el-tooltip
                popper-class="tooltip"
                :hide-after="5000"
                effect="dark"
                placement="right"
            >
                <div slot="content">
                    Item must be insured to at least 80% of value. For more
                    details on coinsurance and purchasing the appropriate limit
                    of coverage,
                    <a href="/faq" target="_blank">please refer to FAQs</a>
                </div>
                <font-awesome-icon :icon="['fal', 'info-circle']" />
            </el-tooltip>
        </FormHeading>
        <div v-if="location" class="location-item" @click.prevent="prev">
            <img :src="location.src" :alt="location.state" />
            <p class="location-label">{{ location.state }}</p>
        </div>
        <div class="table-wrapper" v-if="addedProperties.length">
            <div class="table-header">
                <h3>Covered Property</h3>
                <h3 class="right-align">Limit of Insurance</h3>
            </div>

            <div
                v-for="(item, index) in addedProperties"
                :key="index"
                class="table-row"
            >
                <div class="cell">
                    <div class="row-label">
                        <span v-if="item.year">
                            {{ item.year }}
                        </span>
                        <span>{{ item.description }}</span>
                        <div class="row-bottom">
                            <p v-if="item.serial">{{ item.serial }}</p>
                        </div>
                    </div>
                </div>
                <div class="cell">
                    <p class="row-label right-align">
                        ${{ addComma(item.value) }}
                    </p>
                </div>
            </div>
            <div v-if="addedProperties.length" class="table-row">
                <div class="cell">
                    <div class="row-label">
                        <span>Property #{{ addedProperties.length + 1 }}</span>
                    </div>
                </div>
                <div class="cell">
                    <p class="row-label right-align">
                        ${{ addComma(parseFloat(numberValue)) }}
                    </p>
                </div>
            </div>
            <div class="table-footer">
                <div class="cell">
                    <p class="row-label total-text">
                        Total Property Limit of Insurance
                    </p>
                </div>
                <div class="cell">
                    <p class="row-label total right-align">
                        ${{ addComma(totalValue) }}
                    </p>
                </div>
            </div>
        </div>
        <p class="input-label">Property #{{ property }}</p>
        <div class="amount-wrapper">
            <TextInput
                v-model="propertyValue"
                name="value"
                label="Property Value"
                subDollar
                type="price"
                :focus="true"
                :error="errors['propertyValue']"
                :accountNumber="true"
                @addComma="
                    addCommaValue();
                    computePremium(currentDeductible.value);
                    computePremiumPerProperty(currentDeductible.value);
                "
            />
            <div class="slider-wrapper">
                <input
                    class="value-slider"
                    v-model="numberValue"
                    type="range"
                    min="0"
                    max="100000"
                    v-on:input="
                        computePremium(currentDeductible.value);
                        computePremiumPerProperty(currentDeductible.value);
                    "
                />
                <div class="track-fill" :style="trackStyle"></div>
                <div class="track"></div>
            </div>
        </div>
        <p v-if="totalValue <= 100000" class="input-sub-label">Deductible</p>
        <div v-if="totalValue <= 100000" class="deductible-options">
            <Button
                v-for="item in deductibles"
                :disabled="isMinimumPremium(item)"
                :key="item.id"
                :class="[currentDeductible.id === item.id ? '' : 'outline']"
                @click="
                    setDeductible(item);
                    computePremium(currentDeductible.value);
                    computePremiumPerProperty(currentDeductible.value);
                "
                >${{ addComma(item.value) }}</Button
            >
        </div>

        <p v-if="isMinimumPremiumAlert" class="underwriter-text">
            <span>$500</span> and <span>$1,000</span> deductibles are available
            for property values over <span>${{ minimumPremium }}</span
            >.
        </p>

        <p v-if="totalValue > 100000" class="underwriter-text">
            Dremca can instantly quote and bind insurance for property up to
            <span>$100,000.</span><br />For property in excess of
            <span>$100,000</span> please continue to work through the
            application,<br />and our team will review and respond within one
            business day.
        </p>
        <div v-else>
            <p class="input-sub-label">
                {{ addedProperties.length ? "Total Premium" : "Premium" }}
            </p>
            <p class="premium-amount">
                <template v-if="premiumBilling.value == 'monthly'">
                    ${{ monthlyPremium }}
                    <span class="amount-label">monthly</span>
                </template>
                <template v-else>
                    ${{ yearlyPremium }}
                    <span class="amount-label">yearly</span>
                </template>
            </p>
            <div class="billing-options">
                <FormGroup>
                    <RadioButtons
                        :value="premiumBilling"
                        name="premiumBilling"
                        label
                        @change="premiumBilling = $event"
                        :items="premiumBillingOptions"
                        :strongText="true"
                    />
                </FormGroup>
            </div>
        </div>

        <div class="button-group">
            <Button class="transparent" @click="prev">Back</Button>
            <Button class="red" @click="checkForm">Continue</Button>
        </div>
    </div>
</template>

<script>
import {
  FormHeading,
  Button,
  TextInput,
  FormGroup,
  RadioButtons
} from "../../style";
import { PROPERTY_LOCATIONS } from "../../utils/constants";
import { addCommaToNumber, replaceComma, scrollTop } from "../../utils";
export default {
  name: "PropertyValue",
  props: {
    step: {
      type: Number,
      required: true
    },
    properties: {
      type: Array,
      default: () => []
    },
    property: {
      type: Number,
      default: 1
    },
    payment: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    FormHeading,
    Button,
    TextInput,
    FormGroup,
    RadioButtons
  },
  computed: {
    location() {
      if (this.properties.length) {
        const state = this.properties[0].locationState;
        const location = PROPERTY_LOCATIONS.filter(loc => {
          return loc.abbrev == state;
        })[0];
        return location;
      }
      return null;
    },
    premiumBillingOptions() {
      let monthlyPremium = this.monthlyPremium;
      let yearlyPremium = this.yearlyPremium;
      return [
        {
          value: "monthly",
          row_val: monthlyPremium,
          display: "Monthly $" + monthlyPremium
        },
        {
          value: "full",
          row_val: yearlyPremium,
          display: "Yearly $" + Math.round(yearlyPremium)
        }
      ];
    },
    monthlyPremium() {
      return (this.premium / 12).toFixed(2);
    },
    yearlyPremium() {
      return this.addComma(Math.round(this.premium));
    },
    highPremiumRate() {
      let locationAbbrev = this.location.abbrev;
      return locationAbbrev === "NY" ||
                locationAbbrev === "MA" ||
                locationAbbrev === "CT" ||
                locationAbbrev === "RI"
        ? true
        : false;
    },
    totalValue() {
      let value = parseFloat(replaceComma(this.numberValue.toString()));
      if (this.properties.length) {
        value = this.computeTotalPropValue(value);
      }
      return value;
    },
    totalPropertiesValue() {
      let value = 0;
      if (this.properties.length) {
        value = this.computeTotalPropValue(value);
      }
      return this.addComma(value.toFixed(2));
    },
    totalDeductible() {
      return this.addComma(this.payment.deductible.toFixed(2));
    },
    totalYearlyPremium() {
      return this.addComma(this.payment.yearlyPremium.toFixed(2));
    },
    addedProperties() {
      return this.properties.filter((property, index) => {
        return index < this.property - 1;
      });
    },
    isMinimumPremiumAlert() {
      return this.highPremiumRate
        ? this.totalValue <= 50000
        : this.totalValue <= 40000;
    },
    minimumPremium() {
      return this.highPremiumRate
        ? this.addComma(50000)
        : this.addComma(40000);
    },
    sumValue() {
      let value = 0;
      let propertyValue = replaceComma(this.propertyValue);
      if (this.properties.length) {
        value = this.computeTotalPropValue(value);
      }
      return parseFloat(value) + parseFloat(propertyValue);
    },
    trackStyle() {
      return (
        "width: " +
                (parseFloat(replaceComma(this.propertyValue)) / 100000) * 100 +
                "%"
      );
    }
  },
  mounted() {
    if (this.addedProperties.length) {
      scrollTop();
    }
    this.computePremium();
  },
  data() {
    return {
      propertyValue: "",
      numberPropertyValue: "",
      numberValue: 0,
      currentDeductible: { id: 1, value: 250 },
      deductibles: [
        { id: 1, value: 250 },
        { id: 2, value: 500 },
        { id: 3, value: 1000 }
      ],
      premium: 350,
      premiumPerProperty: 350,
      premiumBilling: { value: "monthly", row_val: "" },
      errors: {
        propertyValue: ""
      }
    };
  },
  methods: {
    checkForm() {
      let preventSubmit = false;
      if (
        this.propertyValue.trim().length === 0 ||
                parseInt(this.propertyValue) === 0
      ) {
        this.errors["propertyValue"] = "Property value is required.";
        document
          .getElementsByClassName("amount-wrapper")[0]
          .scrollIntoView({ behavior: "smooth", block: "center" });
        preventSubmit = true;
      }
      if (!preventSubmit) {
        this.next();
      }
    },
    next() {
      const propertyValue = {
        value: parseFloat(replaceComma(this.propertyValue)),
        premium: parseFloat(this.premiumPerProperty)
      };
      const payment = {
        yearlyPremium: parseFloat(this.premium),
        deductible: this.currentDeductible.value,
        premiumBilling: this.premiumBilling.value,
        referToUnderWriter:
                    parseFloat(
                      replaceComma(this.payment.totalValue.toString())
                    ) > 100000
                      ? 1
                      : 0
      };
      this.$emit("format-property", propertyValue);
      this.$emit("format-payment", payment);
      this.$emit("next-step");
    },
    prev() {
      if (this.properties.length === 1) {
        this.$emit("prev-step");
      } else {
        this.$emit("prev-step-coverage", this.numberValue);
      }
    },
    addCommaValue() {
      this.propertyValue = addCommaToNumber(this.propertyValue);
    },
    setDeductible(deductible) {
      this.currentDeductible = deductible;
    },
    computePremium(deductible) {
      this.errors["propertyValue"] = "";
      let value = isNaN(this.totalValue) ? 0 : this.totalValue;
      let highPremiumRate = this.highPremiumRate;
      !highPremiumRate
        ? value <= 40000
          ? (this.premium = 350)
          : deductible === 250
            ? (this.premium = (value * 0.95) / 100)
            : deductible === 500
              ? (this.premium = (value * 0.903) / 100)
              : deductible === 1000
                ? (this.premium = (value * 0.855) / 100)
                : null
        : highPremiumRate
          ? value <= 50000
            ? (this.premium = 500)
            : deductible === 250
              ? (this.premium = (value * 0.95) / 100)
              : deductible === 500
                ? (this.premium = (value * 0.903) / 100)
                : deductible === 1000
                  ? (this.premium = (value * 0.855) / 100)
                  : null
          : null;

      if (
        (this.highPremiumRate && this.sumValue <= 50000) ||
                (!this.highPremiumRate && this.sumValue <= 40000)
      ) {
        this.currentDeductible = {
          id: 1,
          value: 250
        };
      }

      if (this.highPremiumRate && this.premium < 500) {
        this.premium = 500;
      }
      if (!this.highPremiumRate && this.premium < 350) {
        this.premium = 350;
      }
      this.premium = Math.round(this.premium);
      this.$emit("set-total-value", this.totalValue);
    },
    computePremiumPerProperty(deductible) {
      let value = this.numberValue;
      let highPremiumRate = this.highPremiumRate;
      !highPremiumRate
        ? value <= 40000
          ? (this.premiumPerProperty = 350)
          : deductible === 250
            ? (this.premiumPerProperty = (value * 0.95) / 100)
            : deductible === 500
              ? (this.premiumPerProperty = (value * 0.903) / 100)
              : deductible === 1000
                ? (this.premiumPerProperty = (value * 0.855) / 100)
                : null
        : highPremiumRate
          ? value <= 50000
            ? (this.premiumPerProperty = 500)
            : deductible === 250
              ? (this.premiumPerProperty = (value * 0.95) / 100)
              : deductible === 500
                ? (this.premiumPerProperty = (value * 0.903) / 100)
                : deductible === 1000
                  ? (this.premiumPerProperty = (value * 0.855) / 100)
                  : null
          : null;

      this.premiumPerProperty = Math.round(this.premiumPerProperty);
    },
    addComma(price) {
      return addCommaToNumber(price);
    },
    isMinimumPremium(deductible) {
      return this.highPremiumRate
        ? this.totalValue <= 50000 && deductible.id !== 1
        : this.totalValue <= 40000 && deductible.id !== 1;
    },
    computeTotalPropValue(price) {
      this.properties.forEach((property, index) => {
        if (index < this.property - 1) {
          price += parseFloat(property.value);
        }
      });
      return price;
    }
  },
  watch: {
    propertyValue(newVal) {
      this.numberValue = replaceComma(newVal);
      this.computePremium(this.currentDeductible.value);
      this.computePremiumPerProperty(this.currentDeductible.value);
      this.$emit("set-total-value", 0);
    },
    numberValue(newVal) {
      this.propertyValue = addCommaToNumber(newVal);
    },
    location() {
      let highPremiumRate = this.highPremiumRate;
      if (highPremiumRate) {
        this.premium = 500;
        this.premiumPerProperty = 500;
      } else {
        this.premium = 350;
        this.premiumPerProperty = 350;
      }
    },
    properties: {
      immediate: true,
      handler(newVal) {
        const { value } = newVal[this.property - 1];
        if (value) {
          this.propertyValue = addCommaToNumber(value);
        }
      }
    },
    payment: {
      immediate: true,
      handler(newVal) {
        const { deductible, premiumBilling, yearlyPremium } = newVal;
        if (deductible) {
          this.currentDeductible = this.deductibles.filter(deduc => {
            return parseInt(deduc.value) === parseInt(deductible);
          })[0];
        }
        if (premiumBilling) {
          this.premiumBilling = this.premiumBillingOptions.filter(
            billing => {
              return billing.value === premiumBilling;
            }
          )[0];
        }
        if (yearlyPremium) {
          this.premium = yearlyPremium;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";

.button-group {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    button:last-of-type {
        margin-left: $padding-lg;
    }
}

.row-bottom {
    p {
        display: inline-block;
        font-size: $font-size-xs;
        color: $label-gray;
        margin: 0;
    }
}

.location-item {
    background-color: #f5f5f5;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 200px;
    height: 200px;
    margin: 30px auto;
    &:hover {
        background-color: $background-gray;
    }
}
.location-img {
    min-height: 100px;
    display: flex;
    align-items: center;
}
.location-label {
    margin: $padding-md auto 0;
    font-size: $font-size-sm;
}

.input-label {
    font-size: $font-size-lg;
    color: $dark-gray;
}
.input-sub-label {
    font-size: $font-size-lg;
    color: $dark-gray;
    text-align: center;
    margin: 30px auto $padding-md;
}
.underwriter-text {
    margin: 30px auto;
    background-color: $background-gray;
    padding: $padding-lg $padding-xxl;
    color: $blue-navy;
    text-align: center;
    font-weight: 400;
    font-size: $font-size-md;
    span {
        font-weight: bold;
    }
}
.deductible-options {
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: 400px;
    margin: auto;
}
.amount-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 100%;
    max-width: 400px;
    margin: auto;
}
.block {
    width: 100%;
}
.amount {
    font-size: $font-size-xl;
    color: $dark-gray;
    text-align: center;

    width: 150px;
    margin-right: 12px;
    border: none;
    outline: none;
    background-color: $blue-gray-light;
}
.amount-label {
    font-size: $font-size-xs;
    color: $label-gray;
}
.premium-amount {
    font-size: $font-size-xl;
    color: $dark-gray;
    text-align: center;
    margin-top: 0;
}
.billing-options {
    width: 100%;
    max-width: 400px;
    margin: auto;
}

input[type="range"] {
    margin: 6px 0;
    width: 100%;
    height: 8px;
    border: none;
    border-radius: 8px;
    background-color: transparent;
    outline: none;
    -webkit-appearance: none;
    cursor: pointer;
    &::-webkit-slider-runnable-track {
        border-radius: 8px;
        height: 8px;
    }
    &::-webkit-slider-thumb {
        margin-top: -6px;
        width: 20px;
        height: 20px;
        background-color: $alt-red-active;
        border: 0px solid rgba(0, 0, 0, 0);
        border: 0;
        border-radius: 20px;
        cursor: pointer;
        -webkit-appearance: none;
        z-index: 2;
    }
    &::-moz-range-track {
        background-color: transparent;
        border: 0;
        border-radius: 8px;
        width: 100%;
        height: 8px;
        cursor: pointer;
    }
    &::-moz-range-progress {
        background-color: $alt-red-active;
        border-radius: 8px;
        height: 8px;
    }
    &::-moz-range-thumb {
        width: 20px;
        height: 20px;
        background-color: $alt-red-active;
        border: 0px solid rgba(0, 0, 0, 0);
        border: 0;
        border-radius: 20px;
        cursor: pointer;
        z-index: 2;
    }
    &::-ms-track {
        background-color: transparent;
        border-color: transparent;
        border-width: 7px 0;
        color: transparent;
        width: 100%;
        height: 8px;
        cursor: pointer;
    }
    &::-ms-thumb {
        width: 20px;
        height: 20px;
        background-color: $alt-red-active;
        border: 0px solid rgba(0, 0, 0, 0);
        border: 0;
        border-radius: 20px;
        cursor: pointer;
        margin-top: 0px;
        z-index: 2;
    }
}
@supports (-ms-ime-align: auto) {
    input[type="range"] {
        margin: 0;
    }
}
.slider-wrapper {
    width: 100%;
    position: relative;
    z-index: 0;
    display: flex;
    align-items: center;
    height: 25px;
}
.track-fill {
    height: 8px;
    width: 100%;
    background-color: $alt-red-active;
    border-radius: 8px 0 0 8px;
    position: absolute;
    width: 0%;
    z-index: -1;
    max-width: calc(100% - #{$font-size-sm});
}
.track {
    height: 8px;
    width: 100%;
    background-color: $background-gray;
    border-radius: 8px;
    position: absolute;
    z-index: -2;
}
.tooltip {
    a {
        &:visited {
            color: $blue-gray-active;
        }
        &:hover {
            text-decoration: none;
        }
    }
}
</style>
